import { formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';
import { useCallback } from 'react';
import styled from 'styled-components';

const startOfLocalDay = (d: Date): Date => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
};

type Props = {
    selectedDate: Date;
    onChange: (date: Date) => void;
};

export const DateNavigator = ({ selectedDate, onChange }: Props) => {
    const dateString = formatDateAsAnniversary({ dateTime: selectedDate });
    const weekday = selectedDate.toLocaleString('fr-FR', { weekday: 'long' });
    const shortDate = formatDateForDisplay(selectedDate);

    const goToPreviousDay = useCallback(() => {
        const previous = startOfLocalDay(selectedDate);
        previous.setDate(previous.getDate() - 1);
        onChange(previous);
    }, [selectedDate, onChange]);

    const goToNextDay = useCallback(() => {
        const next = startOfLocalDay(selectedDate);
        next.setDate(next.getDate() + 1);
        onChange(next);
    }, [selectedDate, onChange]);

    const handleDateInput = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            if (!value) return;
            const [year, month, day] = value.split('-').map(Number);
            onChange(startOfLocalDay(new Date(year, month - 1, day)));
        },
        [onChange],
    );

    return (
        <Container>
            <NavButton type="button" onClick={goToPreviousDay} aria-label="Jour précédent">
                ‹
            </NavButton>
            <DateLabel>
                <Weekday>{weekday}</Weekday>
                <ShortDate>{shortDate}</ShortDate>
            </DateLabel>
            <DateInput type="date" value={dateString} onChange={handleDateInput} />
            <NavButton type="button" onClick={goToNextDay} aria-label="Jour suivant">
                ›
            </NavButton>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const DateLabel = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 120px;
    align-items: center;
`;

const Weekday = styled.span`
    font-weight: 600;
    text-transform: capitalize;
`;

const ShortDate = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const NavButton = styled.button`
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 18px;
    cursor: pointer;
`;

const DateInput = styled.input`
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    padding: 4px 8px;
    color: ${({ theme }) => theme.textColor};
`;
